// A small, dependency-free Markdown renderer for announcements, plus an HTML→Markdown converter used
// when pasting rich content into the Markdown editor. Rendering is XSS-safe by construction: the source
// is HTML-escaped first, then only a known set of tags is injected (no raw HTML passes through).

function esc(s: string): string {
  // quotes too: a link's address goes inside href="…", and a quote in it must not end the attribute
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// inline spans, run on already-escaped text.
function inline(s: string): string {
  return s
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/(^|[^_])_([^_\n]+)_/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
}

/** Render Markdown to safe HTML. Single line breaks inside a paragraph are kept (as <br>) so plain-text
 *  bodies (and hand-written notes) read the way they were typed. */
export function renderMarkdown(md: string): string {
  if (!md) return '';
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let i = 0;
  let list: 'ul' | 'ol' | null = null;
  let para: string[] = [];
  const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };
  const flushPara = () => { if (para.length) { out.push(`<p>${para.map((l) => inline(esc(l))).join('<br>')}</p>`); para = []; } };

  while (i < lines.length) {
    const line = lines[i]!;
    const t = line.trim();

    if (/^```/.test(t)) {
      flushPara(); closeList();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i]!.trim())) { buf.push(lines[i]!); i++; }
      i++; // skip closing fence
      out.push(`<pre><code>${esc(buf.join('\n'))}</code></pre>`);
      continue;
    }
    if (t === '') { flushPara(); closeList(); i++; continue; }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) { flushPara(); closeList(); out.push('<hr>'); i++; continue; }

    const h = /^(#{1,6})\s+(.*)$/.exec(t);
    if (h) { flushPara(); closeList(); const lvl = Math.min(6, h[1]!.length); out.push(`<h${lvl}>${inline(esc(h[2]!))}</h${lvl}>`); i++; continue; }

    if (/^>\s?/.test(t)) { flushPara(); closeList(); out.push(`<blockquote>${inline(esc(t.replace(/^>\s?/, '')))}</blockquote>`); i++; continue; }

    const ul = /^[-*+]\s+(.*)$/.exec(t);
    if (ul) { flushPara(); if (list !== 'ul') { closeList(); out.push('<ul>'); list = 'ul'; } out.push(`<li>${inline(esc(ul[1]!))}</li>`); i++; continue; }
    const ol = /^\d+[.)]\s+(.*)$/.exec(t);
    if (ol) { flushPara(); if (list !== 'ol') { closeList(); out.push('<ol>'); list = 'ol'; } out.push(`<li>${inline(esc(ol[1]!))}</li>`); i++; continue; }

    closeList();
    para.push(line);
    i++;
  }
  flushPara(); closeList();
  return out.join('\n');
}

// ── HTML → Markdown (for pasting rich content into the Markdown editor) ────────────────────────────
function nodeToMd(node: Node): string {
  let out = '';
  node.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE) { out += n.nodeValue || ''; return; }
    if (n.nodeType !== Node.ELEMENT_NODE) return;
    const el = n as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (tag === 'ul' || tag === 'ol') {
      let idx = 1;
      el.querySelectorAll(':scope > li').forEach((li) => {
        const marker = tag === 'ol' ? `${idx++}. ` : '- ';
        out += `\n${marker}${nodeToMd(li).trim()}`;
      });
      out += '\n';
      return;
    }
    const inner = nodeToMd(el);
    switch (tag) {
      case 'b': case 'strong': out += inner.trim() ? `**${inner.trim()}**` : ''; break;
      case 'i': case 'em': out += inner.trim() ? `*${inner.trim()}*` : ''; break;
      case 'a': { const href = el.getAttribute('href') || ''; out += href ? `[${inner.trim()}](${href})` : inner; break; }
      case 'br': out += '\n'; break;
      case 'p': case 'div': out += `${inner.trim()}\n\n`; break;
      case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6': out += `${'#'.repeat(Number(tag[1]))} ${inner.trim()}\n\n`; break;
      case 'blockquote': out += `> ${inner.trim()}\n\n`; break;
      case 'code': out += `\`${inner.trim()}\``; break;
      case 'pre': out += `\`\`\`\n${(el.textContent || '').trim()}\n\`\`\`\n\n`; break;
      default: out += inner;
    }
  });
  return out;
}

/** Convert pasted HTML to Markdown source. Returns '' when there's nothing usable. */
export function htmlToMarkdown(html: string): string {
  if (typeof document === 'undefined' || !html) return '';
  const root = document.createElement('div');
  root.innerHTML = html;
  return nodeToMd(root).replace(/\n{3,}/g, '\n\n').trim();
}
